import { queryOptions, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { hyperliquidInfo } from '@/lib/hyperliquid/client';
import { perpCoinLabel } from '@/lib/hyperliquid/coins';
import type { PerpAnnotation } from '@/lib/hyperliquid/types';

export type PerpAnnotationByCoin = Record<string, PerpAnnotation>;

export const perpsAnnotationsQueryKey = () => ['perps', 'perpAnnotations'] as const;

// ponytail: deployers free-type categories; collapse the casing and plural drift seen on
// mainnet (FX/fx, stock/stocks). Extend the alias map if new variants show up.
const normalizeCategory = (category: string) => category.toLowerCase().replace(/^stock$/, 'stocks');

export const perpsAnnotationsQueryOptions = () =>
  queryOptions({
    queryKey: perpsAnnotationsQueryKey(),
    queryFn: async ({ signal }) => {
      const annotations = await hyperliquidInfo.perpConciseAnnotations(signal);
      const byCoin: PerpAnnotationByCoin = {};
      for (const [coin, annotation] of annotations) {
        byCoin[coin] = { ...annotation, category: normalizeCategory(annotation.category) };
      }
      return byCoin;
    },
    enabled: typeof window !== 'undefined',
    staleTime: Infinity,
  });

const EMPTY_ANNOTATIONS: PerpAnnotationByCoin = {};

/**
 * Selector-only market categories and keywords, independent of the trading-critical
 * metadata query. Empty until fetched or while failing; TanStack retries with backoff and
 * again when the consumer remounts.
 */
export function usePerpsAnnotations() {
  return useQuery(perpsAnnotationsQueryOptions()).data ?? EMPTY_ANNOTATIONS;
}

/** `perpCoinLabel` over the cached annotations; see it for what must stay on the L1 coin. */
export function usePerpsCoinLabel() {
  const annotationByCoin = usePerpsAnnotations();
  return useCallback(
    (coin: string) => perpCoinLabel(annotationByCoin[coin], coin),
    [annotationByCoin],
  );
}
